import React, { useMemo } from 'react';
import sourceColorMap from './utils/sourceColorMap';

const deviceLabel = {
  desktop: '💻',
  mobile: '📱',
  tablet: '📟',
  unknown: '❓',
};

const timeAgo = (ts) => {
  if (!ts) return '';
  const delta = Date.now() - ts;
  const sec = Math.floor(delta / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
};

export default function GalaxyGateMedium({
  liveCount,
  unique24h,
  uniqueAllTime,
  trafficSources,
  regionStats,
  deviceStats,
  lastArrivals,
  recentEvents,
}) {
  const topSources = useMemo(() => {
    const entries = Object.entries(trafficSources || {});
    return entries
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([src, cnt]) => ({
        src,
        cnt,
        color: sourceColorMap[src] || sourceColorMap.unknown,
      }));
  }, [trafficSources]);

  const sparklinePoints = useMemo(() => {
    const data = (recentEvents || []).filter(
      (d) => Number.isFinite(d?.t) && Number.isFinite(d?.v),
    );
    if (!data.length) return '';
    const minT = data[0].t;
    const maxT = data[data.length - 1].t;
    const span = Math.max(maxT - minT, 1);
    const maxV = Math.max(...data.map((d) => d.v), 1);
    return data
      .map((d) => {
        const x = ((d.t - minT) / span) * 100;
        const y = 100 - (d.v / maxV) * 100;
        return `${x},${y}`;
      })
      .join(' ');
  }, [recentEvents]);

  return (
    <div
      className="gg-medium"
      style={{
        background: 'linear-gradient(135deg,#0c1224,#0f1b33)',
        color: '#e8f0ff',
        borderRadius: 16,
        padding: 16,
        boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
      }}
    >
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 12, alignItems: 'center' }}>
        <Metric label="Visitors (live)" value={liveCount ?? 0} accent="var(--gp-guest, #78c8ff)" />
        <Metric label="Visitors 24h" value={unique24h ?? 0} accent="var(--gp-guest, #78c8ff)" />
        <Metric label="Visitors all-time" value={uniqueAllTime ?? 0} accent="var(--gp-guest, #78c8ff)" />
        <div style={{ fontSize: 12, opacity: 0.75, marginLeft: 'auto' }}>
          At the Galaxy Gate
        </div>
      </div>

      <Card title="Top sources">
        {topSources.length ? (
          topSources.map((s) => (
            <div
              key={s.src}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: s.color,
                }}
              />
              <span style={{ textTransform: 'capitalize' }}>{s.src}</span>
              <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{s.cnt}</span>
            </div>
          ))
        ) : (
          <Muted>No source data</Muted>
        )}
      </Card>

      <Card title="Devices">
        {Object.keys(deviceStats || {}).length ? (
          Object.entries(deviceStats).map(([dev, cnt]) => (
            <div
              key={dev}
              style={{
                display: 'flex',
                gap: 8,
                marginBottom: 4,
                alignItems: 'center',
              }}
            >
              <span>{deviceLabel[dev] || '❔'}</span>
              <span style={{ textTransform: 'capitalize' }}>{dev}</span>
              <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{cnt}</span>
            </div>
          ))
        ) : (
          <Muted>No device data</Muted>
        )}
      </Card>

      <Card title="Regions">
        {Object.keys(regionStats || {}).length ? (
          Object.entries(regionStats)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 4)
            .map(([reg, cnt]) => (
              <div
                key={reg}
                style={{
                  display: 'flex',
                  gap: 8,
                  marginBottom: 4,
                  alignItems: 'center',
                }}
              >
                <span style={{ textTransform: 'uppercase' }}>{reg}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{cnt}</span>
              </div>
            ))
        ) : (
          <Muted>No region data</Muted>
        )}
      </Card>

      <Card title="Sparkline (live)">
        {sparklinePoints ? (
          <svg viewBox="0 0 100 100" style={{ width: '100%', height: 60 }}>
            <polyline
              fill="none"
              stroke="#5cffb0"
              strokeWidth="2"
              points={sparklinePoints}
            />
          </svg>
        ) : (
          <Muted>No activity yet</Muted>
        )}
      </Card>

      <Card title="Last arrivals" style={{ gridColumn: '1 / -1' }}>
        {lastArrivals?.length ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))',
              gap: 8,
            }}
          >
            {lastArrivals.slice(0, 6).map((a) => (
              <div
                key={a.sessionId}
                style={{
                  padding: '8px 10px',
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.05)',
                }}
                title={a.fullId || a.sessionId}
              >
                <div style={{ fontWeight: 700 }}>{a.label}</div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  {a.source || 'unknown'} · {a.region || 'unknown'}
                </div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  Referrer: {a.referrer || 'unknown'}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    opacity: 0.7,
                    display: 'flex',
                    gap: 6,
                    alignItems: 'center',
                  }}
                >
                  <span>
                    {deviceLabel[a.device] || deviceLabel.unknown}
                    {a.device ? ` ${a.device}` : ''}
                  </span>
                  <span>· {timeAgo(a.at)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Muted>No arrivals yet</Muted>
        )}
      </Card>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div
      style={{
        minWidth: 80,
        padding: '8px 10px',
        borderRadius: 12,
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.8 }}>{label}</div>
      <div style={{ fontWeight: 800, color: accent }}>{value}</div>
    </div>
  );
}

function Card({ title, children, style }) {
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 12,
        padding: 10,
        ...style,
      }}
    >
      <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function Muted({ children }) {
  return (
    <div style={{ fontSize: 12, opacity: 0.7, padding: '4px 0' }}>{children}</div>
  );
}
